// Bulk document delete — the client-side half of POST /api/documents/bulk-delete.
//
// The route refuses more than DOC_BULK_DELETE_MAX (500) ids per request: each
// id is a Graph call server-side and the handler runs inline, so an unbounded
// batch would outlive the request. That cap is right for a REQUEST and wrong
// for a SELECTION — "select all" on a deal routinely picks more than 500, and
// refusing the whole delete over it ("At most 500 documents per request (got
// 617)") hands the user a number they have no control over. So the page splits
// the selection into requests the route accepts and merges what each reported,
// the same per-request loop the backfills use. Pure, so it unit-tests without
// the page.

// Mirrors the route's default. If the server's cap is ever LOWERED by env, a
// chunk this size is refused by name and the toast says so — never silently
// deleting a subset.
export const BULK_DELETE_CHUNK = 500;

export function chunkIds(ids, size = BULK_DELETE_CHUNK) {
  const n = Math.max(1, Math.floor(Number(size) || BULK_DELETE_CHUNK));
  const out = [];
  for (let i = 0; i < ids.length; i += n) out.push(ids.slice(i, i + n));
  return out;
}

export function emptyDeleteOutcome() {
  return { requested: 0, deleted: 0, notFound: [], failed: [], sharePointRemoved: 0, sharePointNone: 0, sharePointFailed: [] };
}

// Folds one request's response into the running total. Counts add, lists
// concatenate — so the toast over 617 documents reads exactly as it would have
// over one request, had the route accepted one.
export function mergeDeleteOutcome(acc, res) {
  const a = acc || emptyDeleteOutcome();
  const r = res || {};
  return {
    requested:         a.requested + (Number(r.requested) || 0),
    deleted:           a.deleted + (Number(r.deleted) || 0),
    notFound:          a.notFound.concat(Array.isArray(r.notFound) ? r.notFound : []),
    failed:            a.failed.concat(Array.isArray(r.failed) ? r.failed : []),
    sharePointRemoved: a.sharePointRemoved + (Number(r.sharePointRemoved) || 0),
    sharePointNone:    a.sharePointNone + (Number(r.sharePointNone) || 0),
    sharePointFailed:  a.sharePointFailed.concat(Array.isArray(r.sharePointFailed) ? r.sharePointFailed : []),
  };
}

// Runs the chunks IN SEQUENCE through `post(chunk)`. Sequential, not parallel:
// each request already fans out to 4 workers server-side, and several at once
// would multiply the Graph pressure the route's bound exists to hold. A chunk
// that throws stops the loop — the remaining ids were never sent — and the
// error is returned beside what DID land, so the caller can say both.
export async function deleteInChunks(ids, post, { chunkSize = BULK_DELETE_CHUNK } = {}) {
  let outcome = emptyDeleteOutcome();
  const chunks = chunkIds(ids, chunkSize);
  for (let i = 0; i < chunks.length; i++) {
    try {
      outcome = mergeDeleteOutcome(outcome, await post(chunks[i]));
    } catch (error) {
      const unsent = chunks.slice(i).reduce((n, c) => n + c.length, 0);
      return { outcome, error, unsent, chunks: chunks.length, completedChunks: i };
    }
  }
  return { outcome, error: null, unsent: 0, chunks: chunks.length, completedChunks: chunks.length };
}
